import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { AvField } from 'availity-reactstrap-validation';

const ClientInfoEditor = ({
    userList,
    customerList,
    initialUserId,
    initialCustomerId,
    initialCustomContact,
    isReadOnly,
    onChange
}) => {
    const intl = useIntl();
    const [mode, setMode] = useState('search'); // 'search' | 'edit'
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [lines, setLines] = useState({
        line1: '', // Client Name
        line2: '', // Company Name
        line3: '', // Address
        line4: '', // Postcode
        line5: '', // City
        line6: '', // Country
        line7: '', // BTW Number
        line8: ''  // Email
    });
    const wrapperRef = useRef(null);

    // Initialize state based on props
    useEffect(() => {
        if (initialUserId || initialCustomerId || (initialCustomContact && initialCustomContact.trim() !== '')) {
            setMode('edit');
            // If we have a custom contact string, try to parse it back to lines if it matches our format,
            // otherwise just fill line 1 with the whole thing or split by newline.
            // For now, if we have a selected user/customer, we might want to re-populate from them
            // BUT we should respect the saved customContact if it exists.

            if (initialCustomContact) {
                const splitLines = initialCustomContact.split('\n');
                setLines({
                    line1: splitLines[0] || '',
                    line2: splitLines[1] || '',
                    line3: splitLines[2] || '',
                    line4: splitLines[3] || '',
                    line5: splitLines[4] || '',
                    line6: splitLines[5] || '',
                    line7: splitLines[6] || '',
                    line8: splitLines[7] || ''
                });
            } else {
                // Fallback if only ID is present but no contact string (rare)
                populateFromEntity(initialUserId, initialCustomerId);
            }
        } else {
            setMode('search');
        }
    }, [initialUserId, initialCustomerId, initialCustomContact, userList, customerList]); // Watch for prop changes

    // Handle outside click to close dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const populateFromEntity = (userId, customerId) => {
        let entity = null;
        let type = null;
        if (userId) {
            entity = userList?.find(u => u.id === userId);
            type = 'user';
        } else if (customerId) {
            entity = customerList?.find(c => c.id === customerId);
            type = 'customer';
        }

        if (entity) {
            let newLines = {};
            if (type === 'user') {
                newLines = {
                    line1: `${entity.name || ''} ${entity.surname || ''}`.trim(),
                    line2: entity.contact?.company || '',
                    line3: `${entity.contact?.street || ''} ${entity.contact?.building || ''}`.trim(),
                    line4: entity.contact?.zipCode || '',
                    line5: entity.contact?.city || '',
                    line6: entity.contact?.countryName || '',
                    line7: entity.contact?.btwNumber || '',
                    line8: entity.contact?.email || entity.email || ''
                };
            } else if (type === 'customer') {
                newLines = {
                    line1: `${entity.name || ''} ${entity.surname || ''}`.trim(),
                    line2: '', // CustomerListItem has no company field
                    line3: entity.address || '',
                    line4: '', // CustomerListItem has no zipCode
                    line5: '', // CustomerListItem has no city
                    line6: '', // CustomerListItem has no country
                    line7: '', // CustomerListItem has no vatNumber
                    line8: entity.email || ''
                };
            }
            setLines(newLines);
            triggerChange(newLines, userId, customerId);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setShowDropdown(true);
    };

    const handleSelect = (type, id) => {
        setMode('edit');
        setSearchTerm('');
        setShowDropdown(false);

        const userId = type === 'user' ? id : null;
        const customerId = type === 'customer' ? id : null;

        populateFromEntity(userId, customerId);
    };

    const handleLineChange = (key, value) => {
        const newLines = { ...lines, [key]: value };
        setLines(newLines);
        // Keep the current IDs when editing lines
        triggerChange(newLines, initialUserId, initialCustomerId);
    };

    const handleClear = () => {
        setMode('search');
        setLines({
            line1: '', line2: '', line3: '', line4: '',
            line5: '', line6: '', line7: '', line8: ''
        });
        triggerChange(null, null, null);
    };

    const triggerChange = (currentLines, userId, customerId) => {
        if (!currentLines) {
            onChange({
                customContact: '',
                userId: null,
                customerId: null,
                customerCompany: ''
            });
            return;
        }

        // Join lines with newline
        const contactString = Object.values(currentLines).join('\n');
        onChange({
            customContact: contactString,
            userId: userId,
            customerId: customerId,
            customerCompany: currentLines.line2 || '' // Company name from line2
        });
    };

    const filteredUsers = userList?.filter(u => {
        const fullName = `${u.name || ''} ${u.surname || ''} ${u.contact?.company || ''}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
    }) || [];

    const filteredCustomers = customerList?.filter(c => {
        const fullName = `${c.name || ''} ${c.surname || ''} ${c.companyName || ''}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
    }) || [];

    if (isReadOnly) {
        return (
            <div dangerouslySetInnerHTML={{ __html: initialCustomContact ? initialCustomContact.replace(/\n/g, '<br />') : '' }}></div>
        );
    }

    if (mode === 'search') {
        return (
            <div className="position-relative" ref={wrapperRef}>
                <input
                    type="text"
                    className="form-control"
                    placeholder={intl.formatMessage({ id: 'GENERAL.SEARCH' }) + '...'}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setShowDropdown(true)}
                />
                {showDropdown && searchTerm && (
                    <div className="position-absolute w-100 bg-white shadow rounded mt-1 overflow-auto" style={{ maxHeight: '200px', zIndex: 1000 }}>
                        {filteredUsers.length > 0 && (
                            <>
                                <div className="p-2 bg-light fw-bold">{intl.formatMessage({ id: 'INVOICE.FIELD.DEALER' })}</div>
                                {filteredUsers.map(u => (
                                    <div
                                        key={`u_${u.id}`}
                                        className="p-2 cursor-pointer hover-bg-light"
                                        onClick={() => handleSelect('user', u.id)}
                                    >
                                        {u.name} {u.surname} {u.contact?.company ? `(${u.contact.company})` : ''}
                                    </div>
                                ))}
                            </>
                        )}
                        {filteredCustomers.length > 0 && (
                            <>
                                <div className="p-2 bg-light fw-bold">{intl.formatMessage({ id: 'INVOICE.FIELD.CUSTOMER' })}</div>
                                {filteredCustomers.map(c => (
                                    <div
                                        key={`c_${c.id}`}
                                        className="p-2 cursor-pointer hover-bg-light"
                                        onClick={() => handleSelect('customer', c.id)}
                                    >
                                        {c.name} {c.surname} {c.companyName ? `(${c.companyName})` : ''}
                                    </div>
                                ))}
                            </>
                        )}
                        {filteredUsers.length === 0 && filteredCustomers.length === 0 && (
                            <div className="p-2 text-muted text-center">
                                {intl.formatMessage({ id: 'GENERAL.NO_RESULTS_FOUND' })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="position-relative border rounded p-3 bg-light">
            <button
                type="button"
                className="btn btn-icon btn-sm btn-active-light-danger position-absolute top-0 end-0 m-1"
                onClick={handleClear}
                title={intl.formatMessage({ id: 'GENERAL.CLEAR' })}
            >
                <i className="bi bi-x fs-2"></i>
            </button>

            <div className="d-flex flex-column gap-1">
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="Client Name" value={lines.line1} onChange={(e) => handleLineChange('line1', e.target.value)} />
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="Company Name" value={lines.line2} onChange={(e) => handleLineChange('line2', e.target.value)} />
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="Address" value={lines.line3} onChange={(e) => handleLineChange('line3', e.target.value)} />
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="Postcode" value={lines.line4} onChange={(e) => handleLineChange('line4', e.target.value)} />
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="City" value={lines.line5} onChange={(e) => handleLineChange('line5', e.target.value)} />
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="Country" value={lines.line6} onChange={(e) => handleLineChange('line6', e.target.value)} />
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="BTW Number" value={lines.line7} onChange={(e) => handleLineChange('line7', e.target.value)} />
                <input type="text" className="form-control form-control-sm form-control-solid" placeholder="Email" value={lines.line8} onChange={(e) => handleLineChange('line8', e.target.value)} />
            </div>
        </div>
    );
};

export default ClientInfoEditor;
